/*
 * Talking to /payments on the API.
 *
 * FETCH AND NOT AXIOS, for the reason set out at the top of accountApi.js:
 * apiAuth.js monkey-patches axios.create to attach the ADMINISTRATOR's token
 * to every request bound for our API, and a customer's purchase must not
 * carry an administrator's credential. fetch is native and unpatched.
 *
 * NOTHING HERE KNOWS WHAT ANYTHING COSTS. The catalogue is fetched, the
 * order is created server-side from the server's own prices, and the capture
 * is checked against them there too. A price in this file would be a fourth
 * copy - after PRICING, the PayPal plan and the order - and the one that
 * drifts is always the one the customer reads.
 */

const base = () => String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

async function call(path, { method = "POST", body, token } = {}) {
    const headers = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = "Bearer " + token;

    let res;
    try {
        res = await fetch(base() + "/payments" + path, {
            method: method,
            headers: headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    } catch (err) {
        throw new Error("Could not reach us. Check your connection and try again.");
    }

    let data = null;
    try {
        data = await res.json();
    } catch (err) {
        data = null;
    }

    if (!res.ok) {
        const error = new Error((data && data.error) || "Something went wrong. Try again.");
        error.status = res.status;
        /*
         * Carried through, because 402 is not a failure the caller should
         * treat like the others - it is the paywall, and it comes with a
         * reason and a price list the screen needs.
         */
        error.paywall = (data && data.paywall) || null;
        throw error;
    }
    return data || {};
}

/** What things cost, and which PayPal to talk to. Needs no account. */
export const catalogue = () => call("/catalogue", { method: "GET" });

/** Credits, plan, receipts and the properties already opened. */
export const me = (token) => call("/me", { method: "GET", token: token });

/** Starts a one-off purchase. Returns { orderId }. */
export const createOrder = (sku, token) => call("/paypal/order", { body: { sku: sku }, token: token });

/**
 * Finishes one. Returns { credits, creditsGranted }.
 *
 * SAFE TO CALL TWICE, and it sometimes will be: the SDK's onApprove can fire
 * again after a reload. The server answers the second call with
 * alreadyDone rather than charging or granting anything else.
 */
export const captureOrder = (orderId, token) =>
    call("/paypal/order/" + encodeURIComponent(orderId) + "/capture", { token: token });

/** Activates a plan the buyer approved. The server verifies it with PayPal. */
export const activateSubscription = (subscriptionId, token) =>
    call("/paypal/subscription", { body: { subscriptionId: subscriptionId }, token: token });

/** Ends a plan. Properties already opened stay open. */
export const cancelSubscription = (token) => call("/paypal/subscription/cancel", { token: token });
